import { LogtoProvider, LogtoConfig } from '@logto/react'
import { ReactNode } from 'react'
import { AppConfigService } from '@/services/config'

// Read from runtime config (window.__APP_CONFIG__) first, then build-time env
const appId = AppConfigService.getString('VITE_LOGTO_APP_ID') || ''
const endpoint = AppConfigService.getString('VITE_LOGTO_ENDPOINT') || ''
const apiResource = AppConfigService.getString('VITE_LOGTO_API_RESOURCE') || ''

// Build configuration
const buildTimeConfig: LogtoConfig = {
  endpoint,
  appId,
  resources: apiResource ? [apiResource] : [],
  scopes: ['openid', 'profile', 'email', 'admin'],
}

// Debug: log Logto configuration and source
const runtimeConfig = typeof window !== 'undefined' ? (window as any)?.__APP_CONFIG__ : undefined
console.log('[Logto Config]', {
  endpoint: buildTimeConfig.endpoint,
  appId: buildTimeConfig.appId,
  resources: buildTimeConfig.resources,
  source: runtimeConfig?.VITE_LOGTO_ENDPOINT ? 'runtime (Docker env)' : 'build-time (import.meta.env)',
})

// Clear old Logto storage if app ID has changed
// This prevents issues when switching Logto apps
if (typeof window !== 'undefined' && appId) {
  const storageKeyPrefix = 'logto'
  const storedAppId = localStorage.getItem(`${storageKeyPrefix}:app_id`)
  if (storedAppId && storedAppId !== appId) {
    console.log('[Logto] App ID changed, clearing old storage...')
    // Clear all Logto-related localStorage items
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(storageKeyPrefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }
  // Store current app ID
  localStorage.setItem(`${storageKeyPrefix}:app_id`, appId)
}

// Check if Logto is configured
const isLogtoConfigured = Boolean(endpoint && appId)

interface LogtoProviderWrapperProps {
  children: ReactNode
}

export function LogtoProviderWrapper({ children }: LogtoProviderWrapperProps) {
  // If Logto is not configured, render children directly (development mode)
  if (!isLogtoConfigured) {
    return <>{children}</>
  }

  return (
    <LogtoProvider config={buildTimeConfig}>
      {children}
    </LogtoProvider>
  )
}

// Export configuration check function
export function isLogtoEnabled(): boolean {
  return isLogtoConfigured
}

// Export API resource for token fetching
export function getLogtoApiResource(): string {
  return apiResource
}
